import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { setupInstallPrompt } from "./registerSW";

/**
 * Floating "Install app" button — only renders on supported browsers (Chrome
 * Android / Edge desktop) when the `beforeinstallprompt` event fires.
 * Auto-hides once installed or dismissed.
 */
export default function PWAInstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Permanently dismissed in localStorage?
    if (localStorage.getItem("pwa-install-dismissed") === "1") {
      setDismissed(true);
    }
    const unsub = setupInstallPrompt((e) => setDeferredPrompt(e));
    const onInstalled = () => setDeferredPrompt(null);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      unsub();
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!deferredPrompt || dismissed) return null;

  const handleInstall = async () => {
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch (err) {
      // User dismissed the native prompt or browser blocked it — log but don't crash.
      console.warn("[PWA] install prompt failed:", err);
    } finally {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    // Permanent — user actively said no.
    localStorage.setItem("pwa-install-dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      className="fixed left-4 right-4 sm:right-auto bottom-20 sm:bottom-4 z-[9998] flex items-center justify-between gap-2 bg-[#002FA7] text-white pl-4 pr-1.5 py-2 shadow-lg border border-[#002FA7] max-w-xs mx-auto sm:mx-0"
      data-testid="pwa-install-banner"
      style={{ borderRadius: 999 }}
    >
      <button
        onClick={handleInstall}
        className="flex items-center gap-2 text-sm font-medium whitespace-nowrap"
        data-testid="pwa-install-button"
      >
        <Download size={14} />
        Install app
      </button>
      <button
        onClick={handleDismiss}
        className="text-white/80 hover:text-white w-6 h-6 flex items-center justify-center shrink-0"
        data-testid="pwa-install-dismiss"
        aria-label="Dismiss install prompt"
        style={{ borderRadius: 999 }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
